import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { Icon, MessageBox, Spacing, Text, Button } from '@reservamos/elements';
import { Trans, useTranslation } from 'react-i18next';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { quickPurchaseBuyerEditTracker } from 'metrics/user-analytics/purchase';
import PurchaserFields from './PurchaserFields';
import TaxpayerIdInput from './TaxpayerIdInput';
import BillingRecipientSection from './BillingRecipientSection';

const purchaserSelector = formValueSelector('purchaser');

/**
 * PurchaserForm component.
 * Renders a form section for collecting purchaser information with optional invoice toggle.
 * @param {object} props - showPurchaserForm, initialValues, isExchange, showInvoiceToggle, collapsible.
 * @param {boolean} props.showPurchaserForm - Controls visibility of purchaser form fields.
 * @param {object} props.initialValues - Initial values for the purchaser form.
 * @param {boolean} props.isExchange - Flag indicating if this is an exchange transaction.
 * @param {boolean} props.showInvoiceToggle - Controls visibility of the invoice/taxpayer ID section.
 * @param {boolean} props.collapsible - Shows only phone and email with a MessageBox for identity fields.
 * @param {object} props.initialBillingCompany - Serialized billing-company selection, if present.
 * @param {boolean} props.defaultExpanded - When collapsible, start expanded (e.g. buyer data is incomplete).
 */
const PurchaserForm = ({
  showPurchaserForm,
  initialValues,
  isExchange,
  showInvoiceToggle,
  collapsible,
  initialBillingCompany,
  defaultExpanded,
  purchaserAgreementContent,
}) => {
  const { t } = useTranslation('payment');
  const {
    SHOW_PURCHASER_FORM_BILLING_TITLE: showBillingTitle,
    ENABLE_THIRD_PARTY_INVOICE: enableThirdPartyInvoice,
  } = useWhitelabelFeatures();
  const { brand, documentTypes } = useWhitelabelEnvs();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Self/third-party invoice UI. Opt-in per tenant: it only makes sense where the
  // provider emits the fiscal invoice and can register the recipient itself
  // (e.g. TM Solutions), so it is gated on an explicit flag on top of the
  // tributary document being configured. Every other tenant — including the
  // brands that also declare a NIT, and the RUC tenants — keeps the taxpayer-id
  // input untouched.
  const usesThirdPartyInvoice =
    Boolean(enableThirdPartyInvoice) && Boolean(documentTypes?.purchaser?.tributaryNumber);

  const purchaserName = useSelector((state) => {
    const firstName = purchaserSelector(state, 'purchaserFirstName');
    const lastName = purchaserSelector(state, 'purchaserLastName');
    return [firstName, lastName].filter(Boolean).join(' ');
  });

  const titleText = showBillingTitle
    ? t('purchaser_info_billing', { context: brand })
    : t('purchaser_info');

  const invoiceSection = showInvoiceToggle && (
    <div className="checkout-header">
      <Text size="XL" weight="bold" className="checkout-header-title">
        {t('need_invoice')}
      </Text>
      {usesThirdPartyInvoice ? <BillingRecipientSection /> : <TaxpayerIdInput />}
    </div>
  );

  // The recipient flow needs the identity and address fields hidden while collapsed.
  const showInvoiceWhileCollapsed = Boolean(invoiceSection) && !usesThirdPartyInvoice;

  const renderPurchaserContent = () => {
    if (collapsible && !isExpanded) {
      return (
        <div className="grid gap-3 mb-12">
          {showInvoiceWhileCollapsed && invoiceSection}
          <Text size="XL" weight="bold" className="checkout-header-title">
            {titleText}
          </Text>
          <MessageBox borderColor="warning" borderRadius="L" widthFull>
            <div className="grid gap-4 min-h-[2lh] md:grid-cols-[auto_auto] md:gap-6">
              <Spacing size="XS" alignItems="center">
                <div className="flex items-center gap-2">
                  <Icon type="emojiSparkles" size="S" />
                  <Text size="S">
                    <Trans>{t('quick_checkout_purchaser_message', { name: purchaserName })}</Trans>
                  </Text>
                </div>
              </Spacing>
              <Button
                onClick={() => {
                  quickPurchaseBuyerEditTracker();
                  setIsExpanded(true);
                }}
                isRounded
                buttonType="button"
              >
                <div className="flex items-center gap-1">
                  <Text size="S" weight="semibold" color="accent">
                    {t('edit_data')}
                  </Text>
                  <Icon type="ChevronDown" size="S" color="grayLight" />
                </div>
              </Button>
            </div>
          </MessageBox>
          <PurchaserFields
            hideIdentityFields
            isQuickCheckout
            isExchange={isExchange}
            initialBillingCompany={initialBillingCompany}
            purchaser={initialValues}
            showPurchaserForm={showPurchaserForm}
          />
        </div>
      );
    }

    return (
      <>
        {invoiceSection}
        <div className="checkout-header">
          <Text size="XL" weight="bold" className="checkout-header-title">
            {titleText}
          </Text>
        </div>
        <PurchaserFields
          isQuickCheckout={collapsible}
          isExchange={isExchange}
          initialBillingCompany={initialBillingCompany}
          purchaser={initialValues}
          showPurchaserForm={showPurchaserForm}
        />
      </>
    );
  };

  return (
    <>
      {renderPurchaserContent()}
      {purchaserAgreementContent}
    </>
  );
};

PurchaserForm.propTypes = {
  initialValues: PropTypes.object,
  showPurchaserForm: PropTypes.bool,
  isExchange: PropTypes.bool,
  showInvoiceToggle: PropTypes.bool,
  collapsible: PropTypes.bool,
  initialBillingCompany: PropTypes.object,
  defaultExpanded: PropTypes.bool,
  purchaserAgreementContent: PropTypes.node,
};

PurchaserForm.defaultProps = {
  defaultExpanded: false,
};

export default PurchaserForm;
